import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Paper,
  Avatar,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Collapse,
  Autocomplete
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { amDesignColors, amDesignTypography } from '../themes/amDesignSystem';
import ServiceManager from '../components/ServiceManager';
import {
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Category as CategoryIcon,
  Email as EmailIcon,
  Map as MapIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Event as AppointmentIcon,
  Share as SocialIcon,
  EventBusy as HolidayIcon,
  Build as ServiceIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveAttributesIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  Pinterest as PinterestIcon,
  VideoCall as TikTokIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import FenceIcon from '@mui/icons-material/Fence';

import StoreIcon  from '@mui/icons-material/Store';

const ProfilePage = () => {
  const { locationId } = useParams();
  const { t } = useTranslation();
  const { isReadOnly } = useAuth();
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topLevelTab, setTopLevelTab] = useState(0); // 0 = Profile, 1 = Services
  const [categories, setCategories] = useState(null);
  const [attributes, setAttributes] = useState(null);
  const [businessHours, setBusinessHours] = useState(null);
  const [serviceArea, setServiceArea] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [editingAttributes, setEditingAttributes] = useState(false);
  const [updatedAttributes, setUpdatedAttributes] = useState({});
  const [updatedLanguages, setUpdatedLanguages] = useState({ setValues: [], unsetValues: [] });
  const [updatedRepeatedEnums, setUpdatedRepeatedEnums] = useState({});
  const [attributeMetadata, setAttributeMetadata] = useState(null);
  const [updatingAttributes, setUpdatingAttributes] = useState(false);
  const [editingHours, setEditingHours] = useState(false);
  const [updatedHours, setUpdatedHours] = useState({});
  const [updatingHours, setUpdatingHours] = useState(false);
  const [editingSocialLinks, setEditingSocialLinks] = useState(false);
  const [updatedSocialLinks, setUpdatedSocialLinks] = useState({});
  const [updatingSocialLinks, setUpdatingSocialLinks] = useState(false);
  const [activeSocialLinks, setActiveSocialLinks] = useState([]);
  const [googleToken, setGoogleToken] = useState(null);
  const [editingCategories, setEditingCategories] = useState(false);
  const [updatedCategories, setUpdatedCategories] = useState({});
  const [updatingCategories, setUpdatingCategories] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allCategoriesData, setAllCategoriesData] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categorySearchTimer, setCategorySearchTimer] = useState(null);
  const [editingSpecialHours, setEditingSpecialHours] = useState(false);
  const [updatedSpecialHours, setUpdatedSpecialHours] = useState([]);
  const [updatingSpecialHours, setUpdatingSpecialHours] = useState(false);
  const [editingContactInfo, setEditingContactInfo] = useState(false);
  const [updatedContactInfo, setUpdatedContactInfo] = useState({});
  const [updatingContactInfo, setUpdatingContactInfo] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [updatedAddress, setUpdatedAddress] = useState({});
  const [updatingAddress, setUpdatingAddress] = useState(false);

  // Social links configuration - Using actual attribute names from your system
  const SOCIAL_PLATFORMS = {
    instagram: {
      name: 'Instagram',
      attributeName: 'attributes/url_instagram',
      icon: InstagramIcon,
      color: '#E4405F',
      placeholder: 'https://www.instagram.com/ihrfirma',
      pattern: /^https?:\/\/(www\.)?instagram\.com\/.+/
    },
    facebook: {
      name: 'Facebook',
      attributeName: 'attributes/url_facebook',
      icon: FacebookIcon,
      color: '#1877F2',
      placeholder: 'https://www.facebook.com/ihrfirma',
      pattern: /^https?:\/\/(www\.)?(facebook\.com|fb\.me)\/.+/
    },
    twitter: {
      name: 'X (Twitter)',
      attributeName: 'attributes/url_twitter',
      icon: TwitterIcon,
      color: '#1DA1F2',
      placeholder: 'https://twitter.com/ihrfirma',
      pattern: /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/.+/
    },
    youtube: {
      name: 'YouTube',
      attributeName: 'attributes/url_youtube',
      icon: YouTubeIcon,
      color: '#FF0000',
      placeholder: 'https://www.youtube.com/@ihrfirma',
      pattern: /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/
    },
    linkedin: {
      name: 'LinkedIn',
      attributeName: 'attributes/url_linkedin',
      icon: LinkedInIcon,
      color: '#0077B5',
      placeholder: 'https://www.linkedin.com/company/ihrfirma',
      pattern: /^https?:\/\/(www\.)?linkedin\.com\/.+/
    }
  };

  // Legacy/Other platforms that might use different attribute names
  const LEGACY_SOCIAL_PLATFORMS = {
    pinterest: {
      name: 'Pinterest',
      attributeName: 'attributes/url_pinterest',
      icon: PinterestIcon,
      color: '#BD081C',
      placeholder: 'https://www.pinterest.com/ihrfirma',
      pattern: /^https?:\/\/(www\.)?pinterest\.(com|de|co\.uk)\/.+/
    },
    tiktok: {
      name: 'TikTok',
      attributeName: 'attributes/url_tiktok',
      icon: TikTokIcon,
      color: '#000000',
      placeholder: 'https://www.tiktok.com/@ihrfirma',
      pattern: /^https?:\/\/(www\.)?tiktok\.com\/.+/
    },
    appointment: {
      name: 'Online-Buchung',
      attributeName: 'attributes/url_appointment',
      icon: AppointmentIcon,
      color: '#4CAF50',
      placeholder: 'https://appointments.yourbusiness.com',
      pattern: /^https?:\/\/.+/
    },
    textmessaging: {
      name: 'SMS / Text Messaging',
      attributeName: 'attributes/url_text_messaging',
      icon: PhoneIcon,
      color: '#4CAF50',
      placeholder: 'sms:+4915110639949',
      pattern: /^sms:\+?[\d\-\s]+/
    }
  };

  // Helper function to translate day names to German
  const getDayNameGerman = (dayEnglish) => {
    const dayTranslations = {
      'MONDAY': 'Montag',
      'TUESDAY': 'Dienstag', 
      'WEDNESDAY': 'Mittwoch',
      'THURSDAY': 'Donnerstag',
      'FRIDAY': 'Freitag',
      'SATURDAY': 'Samstag',
      'SUNDAY': 'Sonntag'
    };
    return dayTranslations[dayEnglish] || dayEnglish;
  };

  // Load categories JSON data on component mount
  useEffect(() => {
    loadCategoriesFromJSON();
  }, []);

  useEffect(() => {
    loadGoogleToken();
  }, []);

  useEffect(() => {
    if (googleToken) {
      loadProfileData();
      loadCategories();
      loadAttributes();
      loadBusinessHours();
      loadServiceArea();
      loadContactInfo();
      loadCoordinates();
      // loadAllCategoriesFromGoogle(); // Replaced with JSON file
    }
  }, [locationId, googleToken]);

  useEffect(() => {
    // Load attribute metadata after categories are loaded
    if (googleToken && categories) {
      loadAttributeMetadata();
    }
  }, [categories, googleToken]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (categorySearchTimer) {
        clearTimeout(categorySearchTimer);
      }
    };
  }, [categorySearchTimer]);

  const [updating, setUpdating] = useState(false);

const handleUpdateCoordinates = async (event) => {
  event.preventDefault();
  setUpdating(true);

  try {
    const formData = new FormData(event.currentTarget);
    const lat = parseFloat(formData.get('lat'));
    const lng = parseFloat(formData.get('lng'));

    console.log('📍 Update coordinates request:', { 
      locationId: businessData.googleLocationId, 
      lat, 
      lng 
    });

    // Validation
    if (isNaN(lat) || isNaN(lng)) {
      alert('Please enter valid coordinates');
      return;
    }

    if (!businessData.googleLocationId) {
      alert('❌ No location ID available');
      return;
    }

    const response = await axios.post('/api/update-coordinates', {
      locationId: businessData.googleLocationId,
      lat,
      lng
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    });

    console.log('✅ Update response:', response.data);

    // Update local state immediately
    setBusinessData(prev => ({
      ...prev,
      coordinates: { lat, lng },
      lat: lat,
      lng: lng
    }));
    
    alert('✅ Koordinaten erfolgreich aktualisiert!');
    
  } catch (error) {
    console.error('❌ Error updating coordinates:', error);
    
    // Better error message
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update coordinates';
    
    alert(`❌ Error: ${errorMessage}`);
  } finally {
    setUpdating(false);
  }
};

// In the JSX, update the button:
<Button 
  type="submit" 
  variant="contained" 
  size="small" 
  sx={{ mt: 2 }}
  disabled={updating}
  startIcon={updating ? <CircularProgress size={16} /> : <SaveIcon />}
>
  {updating ? 'Updating...' : 'Update Coordinates'}
</Button>

  const loadProfileData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`📍 Loading profile for locationId: ${locationId}`);
      const response = await axios.get(`/api/businesses/${locationId}/details`);
      console.log('✅ Profile data loaded:', response.data);
      setBusinessData(response.data);
    } catch (err) {
      console.error('Error loading profile:', err);
      const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to load business profile';
      const fullError = `${errorMessage} (Location ID: ${locationId})`;
      setError(fullError);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleToken = async () => {
    try {
      // Always get fresh token using OAuth refresh token
      console.log('🔄 Getting fresh Google Business access token...');
      
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          client_id: '493907863568-mf7ics226e1i79gnm8gs8qdvrk5rafq4.apps.googleusercontent.com',
          client_secret: '0nplHPk3RTPisuI_WBNsdrc5',
          refresh_token: '1//0585XiTxO1-qnCgYIARAAGAUSNwF-L9IrGutl6mckTIDdBUoubEUOagmpN8vpC76Voz5ZmHX-8gxDE5Z583qab08MofPgBk5VRBU',
          grant_type: 'refresh_token'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.access_token) {
          setGoogleToken(data.access_token);
          console.log('✅ Fresh Google Business token obtained via OAuth refresh');
          console.log(`🔑 New token: ${data.access_token.substring(0, 50)}...`);
          console.log(`🔑 Token expires in: ${data.expires_in} seconds`);
          return;
        }
      } else {
        const errorData = await response.json();
        console.error('❌ OAuth refresh failed:', errorData);
      }
    } catch (err) {
      console.error('❌ Error refreshing OAuth token:', err.message);
    }

    // Fallback: Show user-friendly error
    console.error('❌ Unable to get fresh Google Business token');
    alert('⚠️ Failed to refresh Google Business token. Please check OAuth configuration.');
    setGoogleToken(null);
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?readMask=categories`, {
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
        console.log('✅ Categories loaded:', data.categories);
      } else {
        console.error('Failed to load categories:', response.status);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadAttributes = async () => {
    try {
      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}/attributes`, {
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAttributes(data.attributes);
        console.log('✅ Attributes loaded:', data.attributes);
      } else {
        console.error('Failed to load attributes:', response.status);
      }
    } catch (err) {
      console.error('Error loading attributes:', err);
      // Keep the section visible but show error state
      setAttributes([]);
    }
  };

  const loadBusinessHours = async () => {
    try {
      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?readMask=regularHours,specialHours`, {
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBusinessHours(data);
        console.log('✅ Business hours loaded:', data.regularHours?.periods || 'No periods found');
      } else {
        console.error('Failed to load business hours:', response.status);
      }
    } catch (err) {
      console.error('Error loading business hours:', err);
    }
  };

  const loadServiceArea = async () => {
    try {
      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?readMask=serviceArea`, {
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServiceArea(data.serviceArea);
        console.log('✅ Service area loaded:', data.serviceArea);
      } else {
        console.error('Failed to load service area:', response.status);
      }
    } catch (err) {
      console.error('Error loading service area:', err);
    }
  };

  const loadCoordinates = async () => {
    try {
      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?readMask=latlng`, {
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.latlng) {
          setBusinessData(prev => ({
            ...prev,
            coordinates: {
              latitude: data.latlng.latitude,
              longitude: data.latlng.longitude
            }
          }));
          console.log('✅ Coordinates loaded:', data.latlng);
        }
      } else {
        console.error('Failed to load coordinates:', response.status);
      }
    } catch (err) {
      console.error('Error loading coordinates:', err);
    }
  };

  const loadContactInfo = async () => {
    try {
      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?readMask=title,phoneNumbers,websiteUri,profile,storefrontAddress`, {
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContactInfo(data);
        console.log('✅ Contact info loaded:', data);
      } else {
        console.error('Failed to load contact info:', response.status);
      }
    } catch (err) {
      console.error('Error loading contact info:', err);
    }
  };

  const loadAttributeMetadata = async () => {
    try {
      console.log('🔄 Loading attribute metadata...');
      
      // Try to get the primary category to use for attribute metadata lookup
      const primaryCategory = categories?.primaryCategory?.name;
      if (!primaryCategory) {
        console.log('⚠️ No primary category available, using general attribute metadata');
      }

      // Fetch attribute metadata from Google Business Profile API
      const params = new URLSearchParams({
        regionCode: 'DE', // Germany
        languageCode: 'de', // German
        ...(primaryCategory && { categoryName: primaryCategory })
      });

      const response = await fetch(`/api/locations/attributes/metadata?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAttributeMetadata(data);
        console.log('✅ Attribute metadata loaded:', data);
      } else {
        console.error('Failed to load attribute metadata:', response.status);
        // Fallback: Use basic formatting if API fails
        setAttributeMetadata({ attributes: [] });
      }
    } catch (err) {
      console.error('Error loading attribute metadata:', err);
      // Fallback: Use basic formatting if API fails
      setAttributeMetadata({ attributes: [] });
    }
  };

  const updateAttributes = async () => {
    setUpdatingAttributes(true);
    try {
      // Handle languages separately if they have been modified
      const hasLanguageChanges = updatedLanguages.setValues.length > 0 || updatedLanguages.unsetValues.length > 0;
      
      if (hasLanguageChanges) {
        await updateLanguageAttributes();
      }

      // Handle repeated enum attributes (like credit card types)
      if (Object.keys(updatedRepeatedEnums).length > 0) {
        await updateRepeatedEnumAttributes();
      }

      // Handle other boolean attributes
      if (Object.keys(updatedAttributes).length > 0) {
        await updateBooleanAttributes();
      }

      // Reload attributes to get the latest data
      await loadAttributes();
      
      // Reset editing state
      setEditingAttributes(false);
      setUpdatedAttributes({});
      setUpdatedLanguages({ setValues: [], unsetValues: [] });
      setUpdatedRepeatedEnums({});
      
      alert('✅ Service-Optionen erfolgreich aktualisiert!');
    } catch (err) {
      console.error('Error updating attributes:', err);
      alert(`❌ Error updating attributes: ${err.message}`);
    } finally {
      setUpdatingAttributes(false);
    }
  };

  const updateLanguageAttributes = async () => {
    // Get current language attribute
    const currentLanguageAttr = attributes.find(attr => attr.name === 'attributes/languages_spoken');
    
    // Start with current active languages and apply changes
    let newSetValues = [...(currentLanguageAttr?.repeatedEnumValue?.setValues || [])];
    
    // Apply additions from updatedLanguages.setValues
    updatedLanguages.setValues.forEach(lang => {
      if (!newSetValues.includes(lang)) {
        newSetValues.push(lang);
      }
    });
    
    // Apply removals from updatedLanguages.unsetValues  
    updatedLanguages.unsetValues.forEach(lang => {
      newSetValues = newSetValues.filter(l => l !== lang);
    });

    const requestBody = {
      name: `locations/${locationId}/attributes`,
      attributes: [
        {
          name: 'attributes/languages_spoken',
          repeatedEnumValue: {
            setValues: newSetValues
          }
        }
      ]
    };

    console.log('🔍 Debug - Language request body:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}/attributes?attributeMask=attributes/languages_spoken`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${googleToken}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    console.log('🔍 Debug - Language response status:', response.status);

    if (response.ok) {
      console.log('✅ Languages updated successfully');
    } else {
      const errorText = await response.text();
      console.log('🔍 Debug - Language response error:', errorText);
      throw new Error(`Failed to update languages: ${response.status} - ${errorText}`);
    }
  };

  const updateRepeatedEnumAttributes = async () => {
    // Update each repeated enum attribute individually using the correct API format
    for (const [attributeName, values] of Object.entries(updatedRepeatedEnums)) {
      const requestBody = {
        name: `locations/${locationId}/attributes`,
        attributes: [
          {
            name: attributeName,
            valueType: 'REPEATED_ENUM',
            repeatedEnumValue: {
              setValues: values.setValues,
              unsetValues: values.unsetValues
            }
          }
        ]
      };

      console.log(`🔄 Updating repeated enum attribute ${attributeName}:`, requestBody);

      const response = await fetch(`/api/locations/${locationId}/attributes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to update ${attributeName}: ${errorData}`);
      }

      console.log(`✅ Successfully updated repeated enum attribute ${attributeName}`);
    }
  };

  const updateBooleanAttributes = async () => {
    // Update each boolean attribute individually using the correct API format
    for (const [attributeName, value] of Object.entries(updatedAttributes)) {
      const requestBody = {
        name: `locations/${locationId}/attributes`,
        attributes: [
          {
            name: attributeName,
            values: [value]
          }
        ]
      };

      console.log(`🔍 Debug - Updating ${attributeName}:`, JSON.stringify(requestBody, null, 2));

      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}/attributes?attributeMask=${attributeName}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log(`🔍 Debug - ${attributeName} response status:`, response.status);

      if (response.ok) {
        console.log(`✅ ${attributeName} updated successfully`);
      } else {
        const errorText = await response.text();
        console.log(`🔍 Debug - ${attributeName} response error:`, errorText);
        throw new Error(`Failed to update ${attributeName}: ${response.status} - ${errorText}`);
      }
    }
    
    console.log('✅ All boolean attributes updated successfully');
  };

  const handleAttributeToggle = (attributeName, currentValue) => {
    setUpdatedAttributes(prev => ({
      ...prev,
      [attributeName]: !currentValue
    }));
  };

  const handleLanguageToggle = (language, isCurrentlyActive) => {
    setUpdatedLanguages(prev => {
      if (isCurrentlyActive) {
        // Language is currently active, add to unsetValues (to remove it)
        return {
          setValues: prev.setValues.filter(l => l !== language),
          unsetValues: prev.unsetValues.includes(language) ? prev.unsetValues : [...prev.unsetValues, language]
        };
      } else {
        // Language is currently inactive, add to setValues (to activate it)
        return {
          setValues: prev.setValues.includes(language) ? prev.setValues : [...prev.setValues, language],
          unsetValues: prev.unsetValues.filter(l => l !== language)
        };
      }
    });
  };

  const updateBusinessHours = async () => {
    setUpdatingHours(true);
    try {
      // Get current hours and merge with changes
      const currentPeriods = businessHours?.regularHours?.periods || [];
      
      console.log('🔍 Debug - Current updatedHours state:', JSON.stringify(updatedHours, null, 2));
      
      // Create a map of current periods by day
      const periodsByDay = {};
      currentPeriods.forEach(period => {
        periodsByDay[period.openDay] = period;
      });
      
      // Apply changes from updatedHours
      Object.entries(updatedHours).forEach(([day, dayData]) => {
        const dayUpper = day.toUpperCase();
        
        if (dayData.closed) {
          // Remove the day if closed
          delete periodsByDay[dayUpper];
        } else {
          // Update existing period or create new one
          const existingPeriod = periodsByDay[dayUpper];
          
          // Use existing times as defaults, then apply changes
          const openTime = dayData.openTime || (existingPeriod ? `${String(existingPeriod.openTime.hours || 0).padStart(2, '0')}:${String(existingPeriod.openTime.minutes || 0).padStart(2, '0')}` : '09:00');
          const closeTime = dayData.closeTime || (existingPeriod ? `${String(existingPeriod.closeTime.hours || 0).padStart(2, '0')}:${String(existingPeriod.closeTime.minutes || 0).padStart(2, '0')}` : '18:00');
          
          periodsByDay[dayUpper] = {
            openDay: dayUpper,
            openTime: {
              hours: parseInt(openTime.split(':')[0]),
              minutes: parseInt(openTime.split(':')[1])
            },
            closeDay: dayUpper,
            closeTime: {
              hours: parseInt(closeTime.split(':')[0]),
              minutes: parseInt(closeTime.split(':')[1])
            }
          };
        }
      });

      // Convert back to periods array
      const periods = Object.values(periodsByDay);

      const requestBody = {
        regularHours: {
          periods: periods
        }
      };

      console.log('🔍 Debug - Business hours request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?updateMask=regularHours`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('🔍 Debug - Business hours response status:', response.status);

      if (response.ok) {
        console.log('✅ Business hours updated successfully');
        alert('✅ Öffnungszeiten erfolgreich aktualisiert!');
        
        // Reload business hours to get the latest data
        console.log('🔄 Reloading business hours after update...');
        await loadBusinessHours();
        console.log('✅ Business hours reloaded');
        
        // Reset editing state
        setEditingHours(false);
        setUpdatedHours({});
      } else {
        const errorText = await response.text();
        console.log('🔍 Debug - Business hours response error:', errorText);
        alert(`❌ Failed to update business hours: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating business hours:', err);
      alert(`❌ Error updating business hours: ${err.message}`);
    } finally {
      setUpdatingHours(false);
    }
  };

  const handleHoursChange = (day, field, value) => {
    setUpdatedHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const updateSocialLinks = async () => {
    setUpdatingSocialLinks(true);
    try {
      // Convert updatedSocialLinks to Google Business API format
      const attributesToUpdate = Object.entries(updatedSocialLinks)
        .map(([socialType, url]) => {
          const platform = SOCIAL_PLATFORMS[socialType] || LEGACY_SOCIAL_PLATFORMS[socialType];
          if (!platform) return null;

          return {
            name: platform.attributeName,
            uriValues: url.trim() === '' ? [] : [{ uri: url.trim() }] // Empty array to delete, array with URI to add/update
          };
        })
        .filter(attr => attr !== null); // Remove any null entries

      console.log('🔍 Debug - Social links being sent:', JSON.stringify(attributesToUpdate, null, 2));

      // Create attributeMask with specific attribute names
      const attributeMask = attributesToUpdate.map(attr => attr.name).join(',');
      console.log('🔍 Debug - AttributeMask:', attributeMask);

      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}/attributes?attributeMask=${attributeMask}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: attributesToUpdate
        })
      });

      console.log('🔍 Debug - Social links response status:', response.status);

      if (response.ok) {
        console.log('✅ Social links updated successfully');
        alert('✅ Social Media Links erfolgreich aktualisiert!');
        
        // Reload attributes to get the latest data
        await loadAttributes();
        
        // Reset editing state
        setEditingSocialLinks(false);
        setUpdatedSocialLinks({});
      } else {
        const errorText = await response.text();
        console.log('🔍 Debug - Social links response error:', errorText);
        console.error('Failed to update social links:', response.status, errorText);
        alert(`❌ Failed to update social links: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating social links:', err);
      alert(`❌ Error updating social links: ${err.message}`);
    } finally {
      setUpdatingSocialLinks(false);
    }
  };

  const handleSocialLinkChange = (socialType, url) => {
    setUpdatedSocialLinks(prev => ({
      ...prev,
      [socialType]: url
    }));
  };

  const validateSocialUrl = (url, socialType) => {
    if (!url) return true; // Empty URLs are valid (will remove the link)
    
    const platform = SOCIAL_PLATFORMS[socialType] || LEGACY_SOCIAL_PLATFORMS[socialType];
    if (platform && platform.pattern) {
      return platform.pattern.test(url);
    }
    
    // Fallback to general URL validation
    return /^https?:\/\/.+/.test(url);
  };

  // Get active social links from attributes
  const getActiveSocialLinks = () => {
    if (!attributes) return [];
    
    const activeLinks = [];
    const allPlatforms = { ...SOCIAL_PLATFORMS, ...LEGACY_SOCIAL_PLATFORMS };
    Object.keys(allPlatforms).forEach(platformKey => {
      const platform = allPlatforms[platformKey];
      const attr = attributes.find(attr => attr.name === platform.attributeName);
      if (attr && attr.uriValues?.[0]?.uri) {
        activeLinks.push(platformKey);
      }
    });
    return activeLinks;
  };

  // Add a new social link
  const addSocialLink = (platformKey) => {
    setActiveSocialLinks(prev => [...prev, platformKey]);
    setUpdatedSocialLinks(prev => ({ ...prev, [platformKey]: '' }));
  };

  // Remove a social link
  const removeSocialLink = (platformKey) => {
    setActiveSocialLinks(prev => prev.filter(key => key !== platformKey));
    setUpdatedSocialLinks(prev => ({ ...prev, [platformKey]: '' })); // Empty string will delete it
  };

  // Get available social links (not yet added)
  const getAvailableSocialLinks = () => {
    const active = [...getActiveSocialLinks(), ...activeSocialLinks];
    const allPlatforms = { ...SOCIAL_PLATFORMS, ...LEGACY_SOCIAL_PLATFORMS };
    return Object.keys(allPlatforms).filter(key => !active.includes(key));
  };

  const updateCategories = async () => {
    setUpdatingCategories(true);
    try {
      // Format categories for Google Business API - ensure primary category is always included
      const categoriesToUpdate = {
        primaryCategory: {
          name: updatedCategories.primaryCategory !== undefined 
            ? updatedCategories.primaryCategory 
            : categories.primaryCategory?.name || 'categories/gcid:sunroom_contractor'
        }
      };
      
      if (updatedCategories.additionalCategories !== undefined) {
        const additionalCats = updatedCategories.additionalCategories
          .split(',')
          .map(cat => cat.trim())
          .filter(cat => cat)
          .map(cat => ({ name: cat }));
        
        if (additionalCats.length > 0) {
          categoriesToUpdate.additionalCategories = additionalCats;
        }
      } else if (categories.additionalCategories) {
        // Keep existing additional categories if not modified
        categoriesToUpdate.additionalCategories = categories.additionalCategories.map(cat => ({ name: cat.name }));
      }

      console.log('🔍 Debug - Categories to update:', JSON.stringify(categoriesToUpdate, null, 2));

      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?updateMask=categories`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          categories: categoriesToUpdate
        })
      });

      console.log('🔍 Debug - Categories response status:', response.status);

      if (response.ok) {
        console.log('✅ Categories updated successfully');
        alert('✅ Kategorien erfolgreich aktualisiert!');
        
        // Reload categories to get the latest data
        await loadCategories();
        
        // Reset editing state
        setEditingCategories(false);
        setUpdatedCategories({});
        setCategoryOptions([]); // Clear search results
      } else {
        const errorText = await response.text();
        console.log('🔍 Debug - Categories response error:', errorText);
        console.error('Failed to update categories:', response.status, errorText);
        alert(`❌ Failed to update categories: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating categories:', err);
      alert(`❌ Error updating categories: ${err.message}`);
    } finally {
      setUpdatingCategories(false);
    }
  };

  const handleCategoryChange = (field, value) => {
    setUpdatedCategories(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateSpecialHours = async () => {
    setUpdatingSpecialHours(true);
    try {
      const requestBody = {
        specialHours: {
          specialHourPeriods: updatedSpecialHours
        }
      };

      console.log('🔍 Debug - Special hours request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?updateMask=specialHours`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('🔍 Debug - Special hours response status:', response.status);

      if (response.ok) {
        console.log('✅ Special hours updated successfully');
        alert('✅ Sonderöffnungszeiten erfolgreich aktualisiert!');
        
        // Reload business hours to get the latest data
        console.log('🔄 Reloading business hours after special hours update...');
        await loadBusinessHours();
        console.log('✅ Business hours reloaded');
        
        // Reset editing state
        setEditingSpecialHours(false);
        setUpdatedSpecialHours([]);
      } else {
        const errorText = await response.text();
        console.log('🔍 Debug - Special hours response error:', errorText);
        alert(`❌ Failed to update special hours: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating special hours:', err);
      alert(`❌ Error updating special hours: ${err.message}`);
    } finally {
      setUpdatingSpecialHours(false);
    }
  };

  const updateContactInfo = async () => {
    setUpdatingContactInfo(true);
    try {
      const updateMask = [];
      const locationData = {};

      // Handle title update
      if (updatedContactInfo.title !== undefined) {
        updateMask.push('title');
        locationData.title = updatedContactInfo.title;
      }

      // Handle phone number update
      if (updatedContactInfo.phoneNumber !== undefined) {
        updateMask.push('phoneNumbers.primaryPhone');
        locationData.phoneNumbers = {
          primaryPhone: updatedContactInfo.phoneNumber
        };
      }

      // Handle website URL update
      if (updatedContactInfo.websiteUri !== undefined) {
        updateMask.push('websiteUri');
        locationData.websiteUri = updatedContactInfo.websiteUri;
      }

      // Handle profile description update
      if (updatedContactInfo.description !== undefined) {
        updateMask.push('profile.description');
        locationData.profile = {
          description: updatedContactInfo.description
        };
      }

      if (updateMask.length === 0) {
        alert('Keine Änderungen zu speichern');
        return;
      }

      console.log('🔄 Updating contact info:', { updateMask, locationData });

      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?updateMask=${updateMask.join(',')}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(locationData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        console.log('✅ Contact info updated successfully:', updatedData);
        
        // Reload contact info to get the latest data
        await loadContactInfo();
        
        // Reset editing state
        setEditingContactInfo(false);
        setUpdatedContactInfo({});
        
        alert('✅ Kontaktinformationen erfolgreich aktualisiert!');
      } else {
        const errorText = await response.text();
        console.log('🔍 Debug - Contact info update error:', errorText);
        alert(`❌ Failed to update contact info: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating contact info:', err);
      alert(`❌ Error updating contact info: ${err.message}`);
    } finally {
      setUpdatingContactInfo(false);
    }
  };

  const updateAddress = async () => {
    setUpdatingAddress(true);
    try {
      if (Object.keys(updatedAddress).length === 0) {
        alert('Keine Adressänderungen zu speichern');
        return;
      }

      // Build the storefront address object
      const storefrontAddress = {
        regionCode: updatedAddress.regionCode || contactInfo?.storefrontAddress?.regionCode || 'DE',
        postalCode: updatedAddress.postalCode || contactInfo?.storefrontAddress?.postalCode || '',
        locality: updatedAddress.locality || contactInfo?.storefrontAddress?.locality || '',
        addressLines: updatedAddress.addressLines || contactInfo?.storefrontAddress?.addressLines || ['']
      };

      // Only include fields that have values
      if (!storefrontAddress.postalCode) delete storefrontAddress.postalCode;
      if (!storefrontAddress.locality) delete storefrontAddress.locality;
      if (!storefrontAddress.addressLines[0]) delete storefrontAddress.addressLines;

      console.log('🔄 Updating address:', storefrontAddress);

      const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?updateMask=storefrontAddress`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          storefrontAddress: storefrontAddress
        })
      });

      if (response.ok) {
        const updatedData = await response.json();
        console.log('✅ Address updated successfully:', updatedData);
        
        // Reload contact info to get the latest data
        await loadContactInfo();
        
        // Reset editing state
        setEditingAddress(false);
        setUpdatedAddress({});
        
        alert('✅ Adresse erfolgreich aktualisiert!');
      } else {
        const errorText = await response.text();
        console.log('🔍 Debug - Address update error:', errorText);
        alert(`❌ Failed to update address: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating address:', err);
      alert(`❌ Error updating address: ${err.message}`);
    } finally {
      setUpdatingAddress(false);
    }
  };

  const addSpecialHour = () => {
    const newSpecialHour = {
      startDate: { year: 2026, month: 5, day: 1 },
      closed: true
    };
    setUpdatedSpecialHours(prev => [...prev, newSpecialHour]);
  };

  const removeSpecialHour = (index) => {
    setUpdatedSpecialHours(prev => prev.filter((_, i) => i !== index));
  };

  const updateSpecialHour = (index, field, value) => {
    setUpdatedSpecialHours(prev => {
      const updated = [...prev];
      if (field === 'date') {
        const [year, month, day] = value.split('-');
        updated[index].startDate = {
          year: parseInt(year),
          month: parseInt(month),
          day: parseInt(day)
        };
      } else if (field === 'isClosed') {
        if (value) {
          // Closed: Set closed flag and remove times
          updated[index].closed = true;
          delete updated[index].openTime;
          delete updated[index].closeTime;
        } else {
          // Open: Remove closed flag and add default times
          delete updated[index].closed;
          if (!updated[index].openTime) {
            updated[index].openTime = { hours: 9, minutes: 0 };
          }
          if (!updated[index].closeTime) {
            updated[index].closeTime = { hours: 17, minutes: 0 };
          }
        }
      } else if (field === 'openTime' || field === 'closeTime') {
        const [hours, minutes] = value.split(':');
        updated[index][field] = {
          hours: parseInt(hours),
          minutes: parseInt(minutes)
        };
      }
      return updated;
    });
  };

  // Load categories from local JSON file
  const loadCategoriesFromJSON = async () => {
    try {
      console.log('📂 Loading categories from local JSON file...');
      const response = await fetch('/data/categories.json');
      if (response.ok) {
        const data = await response.json();
        setAllCategoriesData(data);
        console.log(`✅ Loaded ${data.totalCategories} categories from JSON (last updated: ${data.lastUpdated})`);
        
        // Convert to the format expected by the existing code
        const formattedCategories = data.categories.map(cat => ({
          label: cat.displayName,
          value: cat.name,
          category: cat
        }));
        setAllCategories(formattedCategories);
        
      } else {
        console.error('❌ Failed to load categories.json:', response.status);
        console.log('⚠️ Falling back to API calls...');
        loadAllCategoriesFromGoogle();
      }
    } catch (error) {
      console.error('❌ Error loading categories from JSON:', error);
      console.log('⚠️ Falling back to API calls...');
      loadAllCategoriesFromGoogle();
    }
  };

  const loadAllCategoriesFromGoogle = async () => {
    try {
      console.log('🔄 Loading ALL Google Business categories from API...');
      let allCategoriesData = [];
      let nextPageToken = '';
      
      // Load all categories with pagination
      let pageCount = 0;
      do {
        pageCount++;
        const url = `https://mybusinessbusinessinformation.googleapis.com/v1/categories?regionCode=DE&languageCode=de&pageSize=100&view=BASIC${nextPageToken ? `&pageToken=${nextPageToken}` : ''}`;
        
        console.log(`📄 Loading categories page ${pageCount}...`);
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${googleToken}`,
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.categories) {
            allCategoriesData.push(...data.categories);
            console.log(`📄 Page ${pageCount}: Added ${data.categories.length} categories`);
          }
          nextPageToken = data.nextPageToken || '';
          
          // Safety limit to avoid infinite loops
          if (pageCount > 50) {
            console.warn('⚠️ Reached page limit (50), stopping pagination');
            break;
          }
        } else {
          console.error(`Failed to load categories page ${pageCount}:`, response.status);
          const errorText = await response.text();
          console.error('Categories API error:', errorText);
          break;
        }
      } while (nextPageToken);

      const allCategoriesFormatted = allCategoriesData.map(cat => ({
        label: cat.displayName,
        value: cat.name,
        category: cat
      }));

      setAllCategories(allCategoriesFormatted);
      console.log(`✅ Loaded ${allCategoriesFormatted.length} total categories from Google Business API`);
      
      // Verify "Anbieter von Sonnenschutz" is included
      const sonnenschutz = allCategoriesFormatted.find(cat => cat.label.includes('Sonnenschutz'));
      if (sonnenschutz) {
        console.log('✅ Found "Anbieter von Sonnenschutz":', sonnenschutz);
      } else {
        console.warn('⚠️ "Anbieter von Sonnenschutz" not found in categories');
      }
      
    } catch (err) {
      console.error('Error loading all categories from Google API:', err);
    }
  };


  // Debounced category search to prevent excessive API calls
  const debouncedCategorySearch = (searchTerm) => {
    console.log(`🎯 debouncedCategorySearch called with: "${searchTerm}"`);
    
    // Clear existing timer
    if (categorySearchTimer) {
      console.log('⏳ Clearing existing search timer');
      clearTimeout(categorySearchTimer);
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      console.log(`⏰ Debounce timer fired, calling loadAvailableCategories("${searchTerm}")`);
      loadAvailableCategories(searchTerm);
    }, 200); // Fast local search
    
    setCategorySearchTimer(timer);
  };

  const loadAvailableCategories = async (searchTerm = '') => {
    console.log(`🔍 Local search triggered for: "${searchTerm}"`);
    
    if (!searchTerm.trim() || searchTerm.length < 1) {
      setCategoryOptions([]);
      return;
    }

    // Check if categories are loaded
    if (!allCategoriesData || !allCategoriesData.categories) {
      console.log('⏳ Categories not loaded yet, waiting...');
      return;
    }

    setLoadingCategories(true);

    try {
      console.log(`🔍 Searching local categories for: "${searchTerm}"`);
      
      const searchLower = searchTerm.toLowerCase();
      
      // Get currently selected categories to exclude from search
      const currentPrimaryCategory = updatedCategories.primaryCategory !== undefined 
        ? updatedCategories.primaryCategory 
        : categories?.primaryCategory?.name;
        
      const currentAdditionalCategories = updatedCategories.additionalCategories !== undefined
        ? updatedCategories.additionalCategories.split(',').map(cat => cat.trim()).filter(Boolean)
        : (categories?.additionalCategories || []).map(cat => cat.name);
      
      const alreadySelectedCategories = [
        currentPrimaryCategory,
        ...currentAdditionalCategories
      ].filter(Boolean);
      
      console.log('🚫 Excluding already selected categories:', alreadySelectedCategories);

      // Filter categories that match the search term (multiple criteria) and exclude already selected
      const foundCategories = allCategoriesData.categories.filter(cat => {
        const name = cat.displayName.toLowerCase();
        
        // Exclude already selected categories
        if (alreadySelectedCategories.includes(cat.name)) {
          return false;
        }
        
        return name.includes(searchLower) || 
               name.startsWith(searchLower) ||
               name.split(' ').some(word => word.startsWith(searchLower)) ||
               name.split(' ').some(word => word.includes(searchLower)) ||
               // Try partial matches for common terms
               (searchLower.includes('fliese') && (name.includes('tile') || name.includes('floor') || name.includes('boden') || name.includes('fliese'))) ||
               (searchLower.includes('sanitär') && (name.includes('plumb') || name.includes('water') || name.includes('install') || name.includes('sanitär'))) ||
               (searchLower.includes('elektro') && (name.includes('elektr') || name.includes('electric') || name.includes('install')));
      });
      
      console.log(`🎯 Found ${foundCategories.length} local matches for "${searchTerm}" (excluded ${alreadySelectedCategories.length} already selected)`);
      
      // Show matches for debugging
      if (foundCategories.length > 0) {
        console.log(`✨ Matches:`, foundCategories.slice(0, 10).map(c => c.displayName));
      }

      const options = foundCategories.map(cat => ({
        label: cat.displayName,
        value: cat.name,
        category: cat
      }));

      setCategoryOptions(options);
      console.log(`✅ Total search results for "${searchTerm}": ${options.length} categories`);
      
      if (options.length === 0) {
        console.warn(`⚠️ No categories found for "${searchTerm}" in local data`);
        
        // Provide smart suggestions based on common search terms
        const suggestions = [];
        
        if (searchLower.includes('fliese') || searchLower.includes('fliesen')) {
          suggestions.push('Try: "Fliesenleger", "Bodenbelag", "Installateur"');
        }
        if (searchLower.includes('sanitär')) {
          suggestions.push('Try: "Sanitärinstallateur", "Installateur", "Klempner"');
        }
        if (searchLower.includes('elektro')) {
          suggestions.push('Try: "Elektriker", "Installateur"');
        }
        
        if (suggestions.length > 0) {
          console.log(`💡 Suggestions for "${searchTerm}":`);
          suggestions.forEach(s => console.log(`   ${s}`));
        }
        
        console.log(`💡 General tips:`);
        console.log(`   - Use broader terms (e.g., "Bau" instead of specific trades)`);
        console.log(`   - Try "Installateur", "Contractor", "Store", "Service"`);
        console.log(`   - Google Business uses standardized international categories`);
      } else {
        console.log(`🔍 Results:`, options.map(c => c.label));
      }
      
    } catch (err) {
      console.error('❌ Error in real-time category search:', err);
      setCategoryOptions([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!businessData) {
    return (
      <Container sx={{ py: 1 }}>
        <Alert severity="warning">No business profile found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      {/* Top-Level Tabs: Profile / Services */}
      <Box sx={{ mb: 3, borderBottom: `2px solid ${amDesignColors.border}` }}>
        <Box sx={{ display: 'flex', gap: 0 }}>
          <Button
            onClick={() => setTopLevelTab(0)}
            sx={{
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: topLevelTab === 0 ? 600 : 500,
              color: topLevelTab === 0 ? amDesignColors.primary : amDesignColors.text.secondary,
              borderBottom: topLevelTab === 0 ? `3px solid ${amDesignColors.primary}` : 'none',
              borderRadius: 0,
              paddingBottom: 1.5,
              marginBottom: '-2px'
            }}
          >
            Profil
          </Button>
          <Button
            onClick={() => setTopLevelTab(1)}
            sx={{
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: topLevelTab === 1 ? 600 : 500,
              color: topLevelTab === 1 ? amDesignColors.primary : amDesignColors.text.secondary,
              borderBottom: topLevelTab === 1 ? `3px solid ${amDesignColors.primary}` : 'none',
              borderRadius: 0,
              paddingBottom: 1.5,
              marginBottom: '-2px',
              marginLeft: 2
            }}
          >
            Services
          </Button>
        </Box>
      </Box>

      {/* Profile Tab Content */}
      {topLevelTab === 0 && (
      <>
      {/* TOP HEADER SECTION */}
      <Box sx={{
        mb: 4,
        p: 3,
        backgroundColor: amDesignColors.white,
        borderRadius: '14px',
        border: `1px solid ${amDesignColors.border}`,
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)'
      }}>
        <Grid container spacing={3} alignItems="flex-start">
          {/* Left: Logo, Name, Category, Address */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              {/* Logo/Avatar */}
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: amDesignColors.primary,
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: amDesignColors.white,
                  flexShrink: 0
                }}
              >
                {businessData?.name?.charAt(0).toUpperCase() || 'B'}
              </Avatar>

              {/* Business Info */}
              <Box sx={{ flex: 1 }}>
                {/* Name */}
                <Typography variant="h3" sx={{
                  mb: 0.5,
                  fontSize: '1.75rem',
                  fontWeight: 800,
                  color: amDesignColors.text.primary
                }}>
                  {businessData?.name || contactInfo?.title || 'Business Name'}
                </Typography>

                {/* Category Label */}
                {categories?.primaryCategory && (
                  <Box sx={{ mb: 1.5 }}>
                    <Chip
                      label={categories.primaryCategory.displayName}
                      sx={{
                        backgroundColor: amDesignColors.primary,
                        color: amDesignColors.white,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 'auto',
                        padding: '6px 12px'
                      }}
                    />
                  </Box>
                )}

                {/* Address Info */}
                {contactInfo?.storefrontAddress && (
                  <Typography variant="body2" sx={{
                    color: amDesignColors.text.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <LocationIcon sx={{ fontSize: '1rem' }} />
                    {contactInfo?.storefrontAddress?.locality && `${contactInfo.storefrontAddress.locality}`}
                    {contactInfo?.storefrontAddress?.postalCode && ` - ${contactInfo.storefrontAddress.postalCode}`}
                    {contactInfo?.storefrontAddress?.regionCode && ` - ${contactInfo.storefrontAddress.regionCode}`}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right: Profile Completeness */}
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" sx={{
                color: amDesignColors.text.secondary,
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: '0.1em'
              }}>
                Profil-Vollständigkeit
              </Typography>

              {/* Percentage */}
              <Typography variant="h4" sx={{
                color: amDesignColors.primary,
                fontWeight: 800,
                fontSize: '1.5rem'
              }}>
                {businessData?.profileCompleteness || 75}%
              </Typography>

              {/* Progress Bar */}
              <Box sx={{
                width: '100%',
                height: 8,
                backgroundColor: amDesignColors.border,
                borderRadius: 4,
                overflow: 'hidden'
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${businessData?.profileCompleteness || 75}%`,
                  backgroundColor: amDesignColors.success,
                  transition: 'width 0.3s ease'
                }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Contact & Hours */}
        <Grid item xs={12} md={6}>
          {/* KONTAKT CARD */}
          <Card sx={{
            mb: 3,
            backgroundColor: amDesignColors.white,
            borderRadius: '14px',
            border: `1px solid ${amDesignColors.border}`,
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.1em',
                  color: amDesignColors.text.primary
                }}>
                  <PhoneIcon sx={{ fontSize: '1.1rem' }} /> Kontakt
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {editingContactInfo ? (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setEditingContactInfo(false);
                          setUpdatedContactInfo({});
                        }}
                      >
                        Abbrechen
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={updateContactInfo}
                        disabled={updatingContactInfo || Object.keys(updatedContactInfo).length === 0}
                        startIcon={updatingContactInfo ? <CircularProgress size={16} /> : <SaveIcon />}
                      >
                        {updatingContactInfo ? 'Speichern...' : `Änderungen speichern (${Object.keys(updatedContactInfo).length})`}
                      </Button>
                    </>
                  ) : !isReadOnly() ? (
                    <IconButton
                      size="small"
                      onClick={() => setEditingContactInfo(true)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  ) : null}
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                {editingContactInfo ? (
                  // Edit mode: Show input fields
                  <>
                    {/* Business Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <BusinessIcon color="primary" sx={{ minWidth: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Firmenname
                          {updatedContactInfo.title !== undefined && (
                            <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                              (Geändert)
                            </Typography>
                          )}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Firmenname"
                          value={
                            updatedContactInfo.title !== undefined 
                              ? updatedContactInfo.title 
                              : (contactInfo?.title || businessData?.name || '')
                          }
                          onChange={(e) => setUpdatedContactInfo(prev => ({ ...prev, title: e.target.value }))}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>

                    {/* Phone Number */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PhoneIcon color="primary" sx={{ minWidth: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Telefonnummer
                          {updatedContactInfo.phoneNumber !== undefined && (
                            <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                              (Geändert)
                            </Typography>
                          )}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="+49-123-456-7890"
                          value={
                            updatedContactInfo.phoneNumber !== undefined 
                              ? updatedContactInfo.phoneNumber 
                              : (contactInfo?.phoneNumbers?.primaryPhone || businessData?.phoneNumber || '')
                          }
                          onChange={(e) => setUpdatedContactInfo(prev => ({ ...prev, phoneNumber: e.target.value }))}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>

                    {/* Website */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <WebsiteIcon color="primary" sx={{ minWidth: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Website-URL
                          {updatedContactInfo.websiteUri !== undefined && (
                            <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                              (Geändert)
                            </Typography>
                          )}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="https://www.yourbusiness.com"
                          value={
                            updatedContactInfo.websiteUri !== undefined 
                              ? updatedContactInfo.websiteUri 
                              : (contactInfo?.websiteUri || businessData?.websiteUrl || '')
                          }
                          onChange={(e) => setUpdatedContactInfo(prev => ({ ...prev, websiteUri: e.target.value }))}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>

                    {/* Profile Description */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <BusinessIcon color="primary" sx={{ minWidth: 24, mt: 1 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Firmenbeschreibung
                          {updatedContactInfo.description !== undefined && (
                            <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                              (Geändert)
                            </Typography>
                          )}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={4}
                          placeholder="Beschreiben Sie Ihr Unternehmen, Ihre Dienstleistungen und was Sie besonders macht..."
                          value={
                            updatedContactInfo.description !== undefined 
                              ? updatedContactInfo.description 
                              : (contactInfo?.profile?.description || businessData?.description || '')
                          }
                          onChange={(e) => setUpdatedContactInfo(prev => ({ ...prev, description: e.target.value }))}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </>
                ) : (
                  // View mode: Show existing fields
                  <>
                    {/* Business Name */}
                    {contactInfo?.title && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <BusinessIcon color="primary" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Firmenname 
                          </Typography>
                          <Typography variant="body2">
                            {contactInfo.title}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Google API Phone Number */}
                    {contactInfo?.phoneNumbers?.primaryPhone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PhoneIcon color="primary" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Phone 
                          </Typography>
                          <Typography variant="body2">
                            {contactInfo.phoneNumbers.primaryPhone}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Fallback to DB phone if Google API not available */}
                    {!contactInfo?.phoneNumbers?.primaryPhone && businessData.phoneNumber && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PhoneIcon color="primary" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Phone (Database)
                          </Typography>
                          <Typography variant="body2">
                            {businessData.phoneNumber}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Google API Website */}
                    {contactInfo?.websiteUri && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <WebsiteIcon color="primary" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Website: 
                          </Typography>
                          <Typography
                            component="a"
                            href={contactInfo.websiteUri}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {contactInfo.websiteUri}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Fallback to DB website if Google API not available */}
                    {!contactInfo?.websiteUri && businessData.websiteUrl && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <WebsiteIcon color="primary" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Website (Database)
                          </Typography>
                          <Typography
                            component="a"
                            href={businessData.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {businessData.websiteUrl}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Profile Description */}
                    {contactInfo?.profile?.description && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <BusinessIcon color="primary" sx={{ mt: 0.5 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Firmenbeschreibung 
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                            {contactInfo.profile.description}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {businessData.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EmailIcon color="primary" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            E-Mail
                          </Typography>
                          <Typography variant="body2">
                            {businessData.email}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {businessData.contactCloudTenantId && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                          Cloud Tenant ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all' }}>
                          {businessData.contactCloudTenantId}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* ÖFFNUNGSZEITEN CARD - Moved to Left Column */}
          <Card sx={{
            mb: 3,
            backgroundColor: amDesignColors.white,
            borderRadius: '14px',
            border: `1px solid ${amDesignColors.border}`,
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.1em',
                  color: amDesignColors.text.primary
                }}>
                  <ScheduleIcon sx={{ fontSize: '1.1rem' }} /> Öffnungszeiten
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {editingHours ? (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setEditingHours(false);
                          setUpdatedHours({});
                        }}
                      >
                        Zeiten abbrechen
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={updateBusinessHours}
                        disabled={updatingHours || Object.keys(updatedHours).length === 0}
                        startIcon={updatingHours ? <CircularProgress size={16} /> : <SaveAttributesIcon />}
                      >
                        {updatingHours ? 'Speichern...' : `Zeiten speichern (${Object.keys(updatedHours).length})`}
                      </Button>
                    </>
                  ) : !isReadOnly() ? (
                    <IconButton
                      size="small"
                      onClick={() => setEditingHours(true)}
                      color="primary"
                      title="Edit Regular Hours"
                    >
                      <EditIcon />
                    </IconButton>
                  ) : null}
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {businessHours?.regularHours ? (
                <Box sx={{ mb: 3 }}>
                  <Stack spacing={1.5}>
                    {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map((day) => {
                      const period = businessHours.regularHours.periods?.find(p => p.openDay === day);
                      const currentOpenTime = period ? `${String(period.openTime.hours || 0).padStart(2, '0')}:${String(period.openTime.minutes || 0).padStart(2, '0')}` : '09:00';
                      const currentCloseTime = period ? `${String(period.closeTime.hours || 0).padStart(2, '0')}:${String(period.closeTime.minutes || 0).padStart(2, '0')}` : '18:00';

                      const updatedDay = updatedHours[day.toLowerCase()];
                      const isClosed = updatedDay ? updatedDay.closed : !period;
                      const hasChanges = !!updatedDay;

                      return (
                        <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 100, color: amDesignColors.text.primary }}>
                            {getDayNameGerman(day)}
                            {hasChanges && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>

                          {editingHours ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Switch
                                checked={!isClosed}
                                onChange={(e) => handleHoursChange(day.toLowerCase(), 'closed', !e.target.checked)}
                                size="small"
                                color="primary"
                              />
                              {!isClosed && (
                                <>
                                  <TextField
                                    type="time"
                                    size="small"
                                    value={updatedDay?.openTime || currentOpenTime}
                                    onChange={(e) => handleHoursChange(day.toLowerCase(), 'openTime', e.target.value)}
                                    sx={{ width: 120 }}
                                  />
                                  <Typography variant="body2">to</Typography>
                                  <TextField
                                    type="time"
                                    size="small"
                                    value={updatedDay?.closeTime || currentCloseTime}
                                    onChange={(e) => handleHoursChange(day.toLowerCase(), 'closeTime', e.target.value)}
                                    sx={{ width: 120 }}
                                  />
                                </>
                              )}
                              {isClosed && (
                                <Typography variant="body2" color="error" sx={{ minWidth: 200, textAlign: 'center' }}>
                                  Geschlossen
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Box>
                              {period ? (
                                <Typography variant="body2" sx={{ color: amDesignColors.success, fontWeight: 500 }}>
                                  {currentOpenTime} - {currentCloseTime}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                                  Geschlossen
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {businessHours ? 'Keine regulären Öffnungszeiten verfügbar' : 'Öffnungszeiten laden...'}
                </Typography>
              )}

              {/* Special Hours - Compact */}
              {(businessHours?.specialHours?.specialHourPeriods?.length > 0 || editingSpecialHours) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${amDesignColors.border}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.1em' }}>
                      <HolidayIcon sx={{ fontSize: '0.9rem' }} /> Sonderzeiten
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {editingSpecialHours ? (
                        <>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addSpecialHour}
                            variant="outlined"
                            color="warning"
                          >
                            +
                          </Button>
                          <Button
                            size="small"
                            onClick={updateSpecialHours}
                            disabled={updatingSpecialHours}
                            variant="contained"
                            color="warning"
                          >
                            Speichern
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingSpecialHours(false);
                              setUpdatedSpecialHours([]);
                            }}
                            color="default"
                          >
                            <CloseIcon />
                          </IconButton>
                        </>
                      ) : !isReadOnly() ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingSpecialHours(true);
                            setUpdatedSpecialHours(businessHours?.specialHours?.specialHourPeriods || []);
                          }}
                          color="warning"
                        >
                          <EditIcon />
                        </IconButton>
                      ) : null}
                    </Box>
                  </Box>
                  <Stack spacing={0.5} sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {editingSpecialHours ? (
                      <>
                        {updatedSpecialHours.map((period, index) => {
                          const dateStr = `${period.startDate.year}-${String(period.startDate.month).padStart(2, '0')}-${String(period.startDate.day).padStart(2, '0')}`;
                          return (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: amDesignColors.ghost, borderRadius: 1 }}>
                              <TextField type="date" size="small" value={dateStr} onChange={(e) => updateSpecialHour(index, 'date', e.target.value)} sx={{ width: 130 }} />
                              <Switch checked={period.closed} onChange={(e) => updateSpecialHour(index, 'isClosed', e.target.checked)} size="small" />
                              {!period.closed && (
                                <>
                                  <TextField type="time" size="small" value={period.openTime ? `${String(period.openTime.hours).padStart(2, '0')}:${String(period.openTime.minutes || 0).padStart(2, '0')}` : '09:00'} onChange={(e) => updateSpecialHour(index, 'openTime', e.target.value)} sx={{ width: 100 }} />
                                  <TextField type="time" size="small" value={period.closeTime ? `${String(period.closeTime.hours).padStart(2, '0')}:${String(period.closeTime.minutes || 0).padStart(2, '0')}` : '17:00'} onChange={(e) => updateSpecialHour(index, 'closeTime', e.target.value)} sx={{ width: 100 }} />
                                </>
                              )}
                              <IconButton size="small" onClick={() => removeSpecialHour(index)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          );
                        })}
                        {updatedSpecialHours.length === 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                            Keine Sonderzeiten konfiguriert
                          </Typography>
                        )}
                      </>
                    ) : (
                      businessHours.specialHours.specialHourPeriods.map((period, index) => {
                        const startDate = new Date(period.startDate.year, period.startDate.month - 1, period.startDate.day);
                        const endDate = period.endDate ? new Date(period.endDate.year, period.endDate.month - 1, period.endDate.day) : startDate;
                        return (
                          <Typography key={index} variant="caption" sx={{ display: 'block', py: 0.5, color: period.closed ? 'error.main' : 'success.main', fontWeight: 500 }}>
                            {startDate.toLocaleDateString()}: {period.closed ? 'Geschlossen' : (period.openTime && period.closeTime ? `${String(period.openTime.hours || 0).padStart(2, '0')}:${String(period.openTime.minutes || 0).padStart(2, '0')}-${String(period.closeTime.hours || 0).padStart(2, '0')}:${String(period.closeTime.minutes || 0).padStart(2, '0')}` : 'Sonderzeiten')}
                          </Typography>
                        );
                      })
                    )}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Social Media Links - Hidden for now, can be accessed via collapsible or moved to separate section */}
          {false && <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SocialIcon /> Social Media Links
              
                </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editingSocialLinks ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setEditingSocialLinks(false);
                            setUpdatedSocialLinks({});
                          }}
                        >
                          Abbrechen
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={updateSocialLinks}
                          disabled={updatingSocialLinks || Object.keys(updatedSocialLinks).length === 0}
                          startIcon={updatingSocialLinks ? <CircularProgress size={16} /> : <SaveAttributesIcon />}
                        >
                          {updatingSocialLinks ? 'Speichern...' : `Links speichern (${Object.keys(updatedSocialLinks).length})`}
                        </Button>
                      </>
                    ) : !isReadOnly() ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingSocialLinks(true);
                          // Debug: Show what URLs exist in attributes
                          console.log('🔍 Social URLs in attributes:', attributes.filter(attr => attr.name.includes('url_') && attr.uriValues?.[0]?.uri).map(attr => ({ name: attr.name, url: attr.uriValues[0].uri })));
                          setUpdatedSocialLinks({});
                        }}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  {attributes ? (
                    editingSocialLinks ? (
                    // Edit mode: Show input fields for all social links
                    <>
                      {/* Instagram */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <InstagramIcon sx={{ color: '#E4405F', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Instagram
                            {updatedSocialLinks.instagram !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="https://www.instagram.com/yourbusiness"
                            value={
                              updatedSocialLinks.instagram !== undefined 
                                ? updatedSocialLinks.instagram 
                                : (attributes.find(attr => attr.name === 'attributes/url_instagram')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
                            error={updatedSocialLinks.instagram && !validateSocialUrl(updatedSocialLinks.instagram, 'instagram')}
                            helperText={
                              updatedSocialLinks.instagram && !validateSocialUrl(updatedSocialLinks.instagram, 'instagram')
                                ? 'Please enter a valid Instagram URL'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('instagram', '')}
                          color="error"
                          title="Instagram-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* Facebook */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FacebookIcon sx={{ color: '#1877F2', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Facebook
                            {updatedSocialLinks.facebook !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="https://www.facebook.com/yourbusiness"
                            value={
                              updatedSocialLinks.facebook !== undefined 
                                ? updatedSocialLinks.facebook 
                                : (attributes.find(attr => attr.name === 'attributes/url_facebook')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
                            error={updatedSocialLinks.facebook && !validateSocialUrl(updatedSocialLinks.facebook, 'facebook')}
                            helperText={
                              updatedSocialLinks.facebook && !validateSocialUrl(updatedSocialLinks.facebook, 'facebook')
                                ? 'Please enter a valid Facebook URL'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('facebook', '')}
                          color="error"
                          title="Facebook-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* Twitter */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TwitterIcon sx={{ color: '#1DA1F2', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Twitter/X
                            {updatedSocialLinks.twitter !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="https://twitter.com/yourbusiness or https://x.com/yourbusiness"
                            value={
                              updatedSocialLinks.twitter !== undefined 
                                ? updatedSocialLinks.twitter 
                                : (attributes.find(attr => attr.name === 'attributes/url_twitter')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
                            error={updatedSocialLinks.twitter && !validateSocialUrl(updatedSocialLinks.twitter, 'twitter')}
                            helperText={
                              updatedSocialLinks.twitter && !validateSocialUrl(updatedSocialLinks.twitter, 'twitter')
                                ? 'Please enter a valid Twitter/X URL'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('twitter', '')}
                          color="error"
                          title="Twitter/X-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* YouTube */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <YouTubeIcon sx={{ color: '#FF0000', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            YouTube
                            {updatedSocialLinks.youtube !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="https://www.youtube.com/channel/yourbusiness"
                            value={
                              updatedSocialLinks.youtube !== undefined 
                                ? updatedSocialLinks.youtube 
                                : (attributes.find(attr => attr.name === 'attributes/url_youtube')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('youtube', e.target.value)}
                            error={updatedSocialLinks.youtube && !validateSocialUrl(updatedSocialLinks.youtube, 'youtube')}
                            helperText={
                              updatedSocialLinks.youtube && !validateSocialUrl(updatedSocialLinks.youtube, 'youtube')
                                ? 'Please enter a valid YouTube URL'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('youtube', '')}
                          color="error"
                          title="YouTube-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* LinkedIn */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LinkedInIcon sx={{ color: '#0077B5', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            LinkedIn
                            {updatedSocialLinks.linkedin !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="https://www.linkedin.com/company/yourbusiness"
                            value={
                              updatedSocialLinks.linkedin !== undefined 
                                ? updatedSocialLinks.linkedin 
                                : (attributes.find(attr => attr.name === 'attributes/url_linkedin')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('linkedin', e.target.value)}
                            error={updatedSocialLinks.linkedin && !validateSocialUrl(updatedSocialLinks.linkedin, 'linkedin')}
                            helperText={
                              updatedSocialLinks.linkedin && !validateSocialUrl(updatedSocialLinks.linkedin, 'linkedin')
                                ? 'Please enter a valid LinkedIn URL'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('linkedin', '')}
                          color="error"
                          title="LinkedIn-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* Pinterest */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PinterestIcon sx={{ color: '#BD081C', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Pinterest
                            {updatedSocialLinks.pinterest !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="https://www.pinterest.com/yourbusiness"
                            value={
                              updatedSocialLinks.pinterest !== undefined 
                                ? updatedSocialLinks.pinterest 
                                : (attributes.find(attr => attr.name === 'attributes/url_pinterest')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('pinterest', e.target.value)}
                            error={updatedSocialLinks.pinterest && !validateSocialUrl(updatedSocialLinks.pinterest, 'pinterest')}
                            helperText={
                              updatedSocialLinks.pinterest && !validateSocialUrl(updatedSocialLinks.pinterest, 'pinterest')
                                ? 'Please enter a valid Pinterest URL'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('pinterest', '')}
                          color="error"
                          title="Pinterest-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* TikTok */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TikTokIcon sx={{ color: '#000000', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            TikTok
                            {updatedSocialLinks.tiktok !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="https://www.tiktok.com/@yourbusiness"
                            value={
                              updatedSocialLinks.tiktok !== undefined 
                                ? updatedSocialLinks.tiktok 
                                : (attributes.find(attr => attr.name === 'attributes/url_tiktok')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('tiktok', e.target.value)}
                            error={updatedSocialLinks.tiktok && !validateSocialUrl(updatedSocialLinks.tiktok, 'tiktok')}
                            helperText={
                              updatedSocialLinks.tiktok && !validateSocialUrl(updatedSocialLinks.tiktok, 'tiktok')
                                ? 'Please enter a valid TikTok URL'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('tiktok', '')}
                          color="error"
                          title="TikTok-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* Appointment */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AppointmentIcon sx={{ color: 'primary.main', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Book Appointment
                            {updatedSocialLinks.appointment !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="https://your-booking-site.com"
                            value={
                              updatedSocialLinks.appointment !== undefined 
                                ? updatedSocialLinks.appointment 
                                : (attributes.find(attr => attr.name === 'attributes/url_appointment')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('appointment', e.target.value)}
                            error={updatedSocialLinks.appointment && !validateSocialUrl(updatedSocialLinks.appointment, 'appointment')}
                            helperText={
                              updatedSocialLinks.appointment && !validateSocialUrl(updatedSocialLinks.appointment, 'appointment')
                                ? 'Please enter a valid URL'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('appointment', '')}
                          color="error"
                          title="Terminbuchungs-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* SMS / Text Messaging */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PhoneIcon sx={{ color: '#4CAF50', minWidth: 24 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            SMS / Textnachrichten
                            {updatedSocialLinks.textmessaging !== undefined && (
                              <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                (Geändert)
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="sms:+4915110639949"
                            value={
                              updatedSocialLinks.textmessaging !== undefined 
                                ? updatedSocialLinks.textmessaging 
                                : (attributes.find(attr => attr.name === 'attributes/url_text_messaging')?.uriValues?.[0]?.uri || '')
                            }
                            onChange={(e) => handleSocialLinkChange('textmessaging', e.target.value)}
                            error={updatedSocialLinks.textmessaging && !validateSocialUrl(updatedSocialLinks.textmessaging, 'textmessaging')}
                            helperText={
                              updatedSocialLinks.textmessaging && !validateSocialUrl(updatedSocialLinks.textmessaging, 'textmessaging')
                                ? 'Bitte geben Sie eine gültige SMS-Nummer ein'
                                : 'Leer lassen oder auf Löschen klicken, um diesen Link zu entfernen'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSocialLinkChange('textmessaging', '')}
                          color="error"
                          title="SMS-Link löschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </>
                  ) : (
                    // View mode: Show all valid social links dynamically
                    <>
                      {(() => {
                        const allPlatforms = { ...SOCIAL_PLATFORMS, ...LEGACY_SOCIAL_PLATFORMS };
                        const socialLinksFound = [];
                        
                        // Find all social links that exist in attributes
                        console.log('🔍 Debug - All attributes:', attributes.map(attr => ({ name: attr.name, hasUri: !!attr.uriValues?.[0]?.uri })));
                        console.log('🔍 Debug - All platforms:', allPlatforms);
                        
                        Object.entries(allPlatforms).forEach(([platformKey, platform]) => {
                          const attr = attributes.find(attr => attr.name === platform.attributeName);
                          console.log(`🔍 Debug - ${platformKey}:`, { attributeName: platform.attributeName, found: !!attr, hasUri: !!attr?.uriValues?.[0]?.uri });
                          if (attr && attr.uriValues?.[0]?.uri) {
                            const IconComponent = platform.icon;
                            socialLinksFound.push(
                              <Box key={platformKey} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconComponent sx={{ color: platform.color }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {platform.name}
                                  </Typography>
                                  <Typography
                                    component="a"
                                    href={attr.uriValues[0].uri}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    sx={{ 
                                      color: platform.color, 
                                      textDecoration: 'none', 
                                      '&:hover': { textDecoration: 'underline' },
                                      display: 'block'
                                    }}
                                  >
                                    {attr.uriValues[0].uri}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          }
                        });
                        
                        // Also show any other URL attributes that might exist
                        attributes.forEach(attr => {
                          if (attr.name.includes('url_') && attr.uriValues?.[0]?.uri && 
                              !Object.values(allPlatforms).some(p => p.attributeName === attr.name)) {
                            socialLinksFound.push(
                              <Box key={attr.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <SocialIcon sx={{ color: 'primary.main' }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {attr.name.replace('attributes/', '').replace('url_', '').replace('_', ' ').toUpperCase()}
                                  </Typography>
                                  <Typography
                                    component="a"
                                    href={attr.uriValues[0].uri}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    sx={{ 
                                      color: 'primary.main', 
                                      textDecoration: 'none', 
                                      '&:hover': { textDecoration: 'underline' },
                                      display: 'block'
                                    }}
                                  >
                                    {attr.uriValues[0].uri}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          }
                        });
                        
                        return socialLinksFound.length > 0 ? socialLinksFound : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                            Keine Social Media Links konfiguriert. Klicken Sie auf Bearbeiten, um welche hinzuzufügen!
                          </Typography>
                        );
                      })()}
                    </>
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Social Links laden...
                    </Typography>
                  )}
                </Stack>
            </CardContent>
          </Card>}

          {/* Address Information - Hidden for now */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon /> Adresse
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {editingAddress ? (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setEditingAddress(false);
                          setUpdatedAddress({});
                        }}
                      >
                        Abbrechen
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={updateAddress}
                        disabled={updatingAddress || Object.keys(updatedAddress).length === 0}
                        startIcon={updatingAddress ? <CircularProgress size={16} /> : <SaveIcon />}
                      >
                        {updatingAddress ? 'Speichern...' : `Änderungen speichern (${Object.keys(updatedAddress).length})`}
                      </Button>
                    </>
                  ) : !isReadOnly() ? (
                    <IconButton
                      size="small"
                      onClick={() => setEditingAddress(true)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  ) : null}
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                {editingAddress ? (
                  // Edit mode: Show input fields
                  <>
                    {/* Street Address */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Straße
                        {updatedAddress.addressLines !== undefined && (
                          <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                            (Modified)
                          </Typography>
                        )}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Osterstraße 1"
                        value={
                          updatedAddress.addressLines !== undefined 
                            ? (updatedAddress.addressLines[0] || '') 
                            : (contactInfo?.storefrontAddress?.addressLines?.[0] || '')
                        }
                        onChange={(e) => setUpdatedAddress(prev => ({ 
                          ...prev, 
                          addressLines: [e.target.value] 
                        }))}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>

                    {/* City */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Stadt
                        {updatedAddress.locality !== undefined && (
                          <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                            (Modified)
                          </Typography>
                        )}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Hannover"
                        value={
                          updatedAddress.locality !== undefined 
                            ? updatedAddress.locality 
                            : (contactInfo?.storefrontAddress?.locality || '')
                        }
                        onChange={(e) => setUpdatedAddress(prev => ({ 
                          ...prev, 
                          locality: e.target.value 
                        }))}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>

                    {/* Postal Code */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Postleitzahl
                        {updatedAddress.postalCode !== undefined && (
                          <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                            (Modified)
                          </Typography>
                        )}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="30159"
                        value={
                          updatedAddress.postalCode !== undefined 
                            ? updatedAddress.postalCode 
                            : (contactInfo?.storefrontAddress?.postalCode || '')
                        }
                        onChange={(e) => setUpdatedAddress(prev => ({ 
                          ...prev, 
                          postalCode: e.target.value 
                        }))}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>

                    {/* Country */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Ländercode
                        {updatedAddress.regionCode !== undefined && (
                          <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                            (Modified)
                          </Typography>
                        )}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="DE"
                        value={
                          updatedAddress.regionCode !== undefined 
                            ? updatedAddress.regionCode 
                            : (contactInfo?.storefrontAddress?.regionCode || 'DE')
                        }
                        onChange={(e) => setUpdatedAddress(prev => ({ 
                          ...prev, 
                          regionCode: e.target.value.toUpperCase() 
                        }))}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </>
                ) : (
                  // View mode: Show current address
                  <>
                    {/* Google Business Address */}
                    {contactInfo?.storefrontAddress && (
                      <Stack spacing={1}>
                        {contactInfo.storefrontAddress.addressLines?.[0] && (
                          <Typography variant="body2">
                            <strong>Straße:</strong> {contactInfo.storefrontAddress.addressLines[0]}
                          </Typography>
                        )}
                        {contactInfo.storefrontAddress.locality && (
                          <Typography variant="body2">
                            <strong>Stadt:</strong> {contactInfo.storefrontAddress.locality}
                          </Typography>
                        )}
                        {contactInfo.storefrontAddress.postalCode && (
                          <Typography variant="body2">
                            <strong>Postleitzahl:</strong> {contactInfo.storefrontAddress.postalCode}
                          </Typography>
                        )}
                        {contactInfo.storefrontAddress.regionCode && (
                          <Typography variant="body2">
                            <strong>Land:</strong> {contactInfo.storefrontAddress.regionCode}
                          </Typography>
                        )}
                      </Stack>
                    )}

                    {/* No address message */}
                    {!contactInfo?.storefrontAddress && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                        Keine Adressinformationen verfügbar. Klicken Sie auf Bearbeiten, um eine Adresse hinzuzufügen!
                      </Typography>
                    )}
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Coordinates Section */}
          <Card sx={{ mb: 3, backgroundColor: amDesignColors.white, borderRadius: '14px', border: `1px solid ${amDesignColors.border}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                  📍 Koordinaten
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}> {console.log('---dd-d-d-',businessData)}
                {businessData?.coordinates?.latitude ? (
                  <>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: amDesignColors.text.secondary }}>
                        Latitude:
                      </Typography>
                      <Typography variant="body2">
                        {businessData?.coordinates?.lat || businessData?.lat || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: amDesignColors.text.secondary }}>
                        Longitude:
                      </Typography>
                      <Typography variant="body2">
                        {businessData?.coordinates?.lng || businessData?.lng || 'N/A'}
                      </Typography>
                    </Box>
                    {editingAttributes && (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: amDesignColors.text.secondary, mt: 1 }}>
                          Koordinaten aktualisieren:
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <TextField
                            label="Latitude"
                            value={updatedAttributes.latitude || businessData?.coordinates?.latitude || businessData?.latitude || ''}
                            onChange={(e) => setUpdatedAttributes({ ...updatedAttributes, latitude: e.target.value })}
                            size="small"
                          />
                          <TextField
                            label="Longitude"
                            value={updatedAttributes.longitude || businessData?.coordinates?.longitude || businessData?.longitude || ''}
                            onChange={(e) => setUpdatedAttributes({ ...updatedAttributes, longitude: e.target.value })}
                            size="small"
                          />
                        </Box>
                        <Button
                          variant="contained"
                          onClick={async () => {
                            try {
                              const response = await fetch(`https://mybusinessbusinessinformation.googleapis.com/v1/locations/${locationId}?updateMask=locationKeywords`, {
                                method: 'PATCH',
                                headers: {
                                  'Authorization': `Bearer ${googleToken}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({})
                              });
                              if (response.ok) {
                                alert('✅ Koordinaten erfolgreich aktualisiert!');
                              }
                            } catch (error) {
                              alert('❌ Fehler beim Aktualisieren der Koordinaten');
                            }
                          }}
                          sx={{ backgroundColor: amDesignColors.primary }}
                        >
                          <SaveIcon sx={{ mr: 1, fontSize: 18 }} />
                          Koordinaten aktualisieren
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                    Keine Koordinaten verfügbar.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Details */}
        <Grid item xs={12} md={6}>
          {/* Business Details */}
          

          {/* Kategorien */}
          {categories && (
            <Card sx={{
              mb: 3,
              backgroundColor: amDesignColors.white,
              borderRadius: '14px',
              border: `1px solid ${amDesignColors.border}`,
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em',
                    color: amDesignColors.text.primary
                  }}>
                    <CategoryIcon sx={{ fontSize: '1.1rem' }} /> Kategorien
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editingCategories ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setEditingCategories(false);
                            setUpdatedCategories({});
                            setCategoryOptions([]); // Clear search results
                          }}
                        >
                          Abbrechen
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={updateCategories}
                          disabled={updatingCategories || Object.keys(updatedCategories).length === 0}
                          startIcon={updatingCategories ? <CircularProgress size={16} /> : <SaveAttributesIcon />}
                        >
                          {updatingCategories ? 'Speichern...' : `Kategorien speichern (${Object.keys(updatedCategories).length})`}
                        </Button>
                      </>
                    ) : !isReadOnly() ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingCategories(true);
                          // Initialize category options with current primary category
                          const currentPrimary = categories?.primaryCategory;
                          if (currentPrimary) {
                            setCategoryOptions([{
                              value: currentPrimary.name,
                              label: currentPrimary.displayName
                            }]);
                          } else {
                            setCategoryOptions([]);
                          }
                        }}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  {editingCategories ? (
                    // Edit mode: Show input fields for categories
                    <>
                      {/* Primary Category Editor */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Hauptkategorie:
                        </Typography>
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={categoryOptions}
                          value={categoryOptions.find(opt => opt.value === (updatedCategories.primaryCategory !== undefined ? updatedCategories.primaryCategory : categories.primaryCategory?.name)) || null}
                          onChange={(event, newValue) => {
                            handleCategoryChange('primaryCategory', newValue?.value || '');
                          }}
                          onInputChange={(event, newInputValue) => {
                            console.log(`🎤 Primary category onInputChange: "${newInputValue}"`);
                            debouncedCategorySearch(newInputValue);
                          }}
                          getOptionLabel={(option) => option.label || ''}
                          loading={loadingCategories}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search for business categories..."
                              helperText="Suchen und wählen Sie Ihre Hauptgeschäftskategorie"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ mt: 0.5 }}
                        />
                        {updatedCategories.primaryCategory !== undefined && (
                          <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                            (Modified)
                          </Typography>
                        )}
                      </Box>

                      {/* Additional Categories Editor */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Zusätzliche Kategorien:
                        </Typography>
                        <Autocomplete
                          fullWidth
                          size="small"
                          multiple
                          freeSolo
                          options={categoryOptions}
                          value={
                            updatedCategories.additionalCategories !== undefined
                              ? updatedCategories.additionalCategories.split(',').map(catName => 
                                  categoryOptions.find(opt => opt.value === catName.trim()) || { label: catName.trim(), value: catName.trim() }
                                ).filter(Boolean)
                              : categories.additionalCategories?.map(cat => ({ label: cat.displayName, value: cat.name })) || []
                          }
                          onChange={(event, newValues) => {
                            const categoryIds = newValues.map(val => val.value || val).join(', ');
                            handleCategoryChange('additionalCategories', categoryIds);
                          }}
                          onInputChange={(event, newInputValue, reason) => {
                            console.log(`🔍 Autocomplete input changed: "${newInputValue}" (reason: ${reason})`);
                            
                            // Only trigger search for user typing, not for other reasons
                            if (reason === 'input' && newInputValue && newInputValue.length >= 2) {
                              console.log(`🔍 Triggering search for: "${newInputValue}"`);
                              debouncedCategorySearch(newInputValue);
                            } else if (reason === 'clear') {
                              // Keep options when clearing
                              console.log(`🔍 Input cleared, keeping options`);
                            } else {
                              console.log(`⏭️ Skipping search (reason: ${reason}, length: ${newInputValue?.length || 0})`);
                            }
                          }}
                          filterOptions={(options) => {
                            // Return all options without filtering - we already filtered them
                            console.log(`🔍 FilterOptions: Showing ${options.length} options`);
                            return options;
                          }}
                          noOptionsText={`No categories found for your search. Try broader terms like "Bau", "Store", or "Contractor".`}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option.label || option.value || '';
                          }}
                          loading={loadingCategories}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Type to search for additional business categories..."
                              helperText="Suchen und wählen Sie mehrere sekundäre Kategorien"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ mt: 0.5 }}
                        />
                        {updatedCategories.additionalCategories !== undefined && (
                          <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                            (Modified)
                          </Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    // View mode: Show current categories
                    <>
                      {/* Primary Category */}
                      {categories.primaryCategory && (
                        <Box sx={{ mb: categories.additionalCategories?.length > 0 ? 2 : 0 }}>
                          <Chip
                            label={categories.primaryCategory.displayName}
                            sx={{
                              backgroundColor: amDesignColors.primary,
                              color: amDesignColors.white,
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              height: 'auto',
                              padding: '8px 16px'
                            }}
                          />
                        </Box>
                      )}

                      {/* Additional Categories */}
                      {categories.additionalCategories && categories.additionalCategories.length > 0 && (
                        <Box>
                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {categories.additionalCategories.map((category, index) => (
                              <Chip
                                key={index}
                                label={category.displayName}
                                variant="outlined"
                                sx={{
                                  borderColor: amDesignColors.border,
                                  color: amDesignColors.text.primary,
                                  fontWeight: 500,
                                  fontSize: '0.8rem',
                                  height: 'auto',
                                  padding: '8px 12px'
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          
          {/* Hidden original Business Hours section */}
          {false && businessHours && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon /> Öffnungszeiten
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editingHours ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setEditingHours(false);
                            setUpdatedHours({});
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={updateBusinessHours}
                          disabled={updatingHours || Object.keys(updatedHours).length === 0}
                          startIcon={updatingHours ? <CircularProgress size={16} /> : <SaveAttributesIcon />}
                        >
                          {updatingHours ? 'Speichern...' : `Zeiten speichern (${Object.keys(updatedHours).length})`}
                        </Button>
                      </>
                    ) : !isReadOnly() ? (
                      <IconButton
                        size="small"
                        onClick={() => setEditingHours(true)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Regular Hours */}
                {businessHours.regularHours && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Reguläre Öffnungszeiten:
                    </Typography>
                    <Stack spacing={2}>
                      {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map((day) => {
                        const period = businessHours.regularHours.periods?.find(p => p.openDay === day);
                        const currentOpenTime = period ? `${String(period.openTime.hours || 0).padStart(2, '0')}:${String(period.openTime.minutes || 0).padStart(2, '0')}` : '09:00';
                        const currentCloseTime = period ? `${String(period.closeTime.hours || 0).padStart(2, '0')}:${String(period.closeTime.minutes || 0).padStart(2, '0')}` : '18:00';
                        
                        const updatedDay = updatedHours[day.toLowerCase()];
                        const isClosed = updatedDay ? updatedDay.closed : !period;
                        const hasChanges = !!updatedDay;
                        
                        return (
                          <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 80 }}>
                              {getDayNameGerman(day)}
                              {hasChanges && (
                                <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                  (Geändert)
                                </Typography>
                              )}
                            </Typography>
                            
                            {editingHours ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Switch
                                  checked={!isClosed}
                                  onChange={(e) => handleHoursChange(day.toLowerCase(), 'closed', !e.target.checked)}
                                  size="small"
                                  color="primary"
                                />
                                {!isClosed && (
                                  <>
                                    <TextField
                                      type="time"
                                      size="small"
                                      value={updatedDay?.openTime || currentOpenTime}
                                      onChange={(e) => handleHoursChange(day.toLowerCase(), 'openTime', e.target.value)}
                                      sx={{ width: 120 }}
                                    />
                                    <Typography variant="body2">to</Typography>
                                    <TextField
                                      type="time"
                                      size="small"
                                      value={updatedDay?.closeTime || currentCloseTime}
                                      onChange={(e) => handleHoursChange(day.toLowerCase(), 'closeTime', e.target.value)}
                                      sx={{ width: 120 }}
                                    />
                                  </>
                                )}
                                {isClosed && (
                                  <Typography variant="body2" color="error" sx={{ minWidth: 200, textAlign: 'center' }}>
                                    Geschlossen
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Box>
                                {period ? (
                                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                                    {currentOpenTime} - {currentCloseTime}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="error">
                                    Geschlossen
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {/* Special Hours */}
                {businessHours.specialHours && businessHours.specialHours.specialHourPeriods && businessHours.specialHours.specialHourPeriods.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HolidayIcon /> Sonderöffnungszeiten/Feiertage:
                    </Typography>
                    <Stack spacing={1}>
                      {businessHours.specialHours.specialHourPeriods.map((period, index) => {
                        const startDate = new Date(period.startDate.year, period.startDate.month - 1, period.startDate.day);
                        const endDate = new Date(period.endDate.year, period.endDate.month - 1, period.endDate.day);
                        const isSameDay = startDate.getTime() === endDate.getTime();
                        
                        return (
                          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {isSameDay 
                                ? startDate.toLocaleDateString()
                                : `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                              }
                            </Typography>
                            {period.closed ? (
                              <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                                Closed
                              </Typography>
                            ) : period.openingPeriods ? (
                              <Typography variant="body2" sx={{ color: 'success.main' }}>
                                {period.openingPeriods.map(op => 
                                  `${String(op.openTime?.hours || 0).padStart(2, '0')}:${String(op.openTime?.minutes || 0).padStart(2, '0')} - ${String(op.closeTime?.hours || 0).padStart(2, '0')}:${String(op.closeTime?.minutes || 0).padStart(2, '0')}`
                                ).join(', ')}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'info.main' }}>
                                Special hours
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Fallback to old business hours if Google API data not available */}
          {!businessHours && businessData.businessHours && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon /> Öffnungszeiten (Basis)
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={1}>
                  {Object.entries(businessData.businessHours).map(([dayName, dayData]) => (
                    <Box key={dayName} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                        {dayName}
                      </Typography>
                      {dayData.closed ? (
                        <Typography variant="body2" color="error">
                          Closed
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          Open
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Servicebereich */}
          <Card sx={{
            mb: 3,
            backgroundColor: amDesignColors.white,
            borderRadius: '14px',
            border: `1px solid ${amDesignColors.border}`,
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)'
          }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                color: amDesignColors.text.primary,
                mb: 2
              }}>
                <LocationIcon sx={{ fontSize: '1.1rem' }} /> Einzugsgebiet
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {serviceArea ? (

                <Stack spacing={2}>
                  {/* Business Type */}
                  <Box>
            
                    <Chip
                      label={serviceArea.businessType === 'CUSTOMER_LOCATION_ONLY' ? 'Nur Kundenstandort' : serviceArea.businessType}
                      variant="outlined"
                      color="info"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>

                  {/* Service Areas */}
                  {serviceArea.places?.placeInfos && serviceArea.places.placeInfos.length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{
                        display: 'block',
                        mb: 1.5,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        color: amDesignColors.text.secondary
                      }}>
                        {serviceArea.places.placeInfos.length} Standorte
                      </Typography>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: 1,
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        {serviceArea.places.placeInfos.map((place, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              p: 1,
                              backgroundColor: amDesignColors.paper,
                              borderRadius: '8px',
                              border: `1px solid ${amDesignColors.border}`,
                              fontSize: '0.8rem'
                            }}
                          >
                            <LocationIcon sx={{ fontSize: '0.9rem', color: amDesignColors.primary, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: amDesignColors.text.primary }}>
                              {place.placeName.replace(', Deutschland', '')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Region */}
                  {serviceArea.regionCode && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Region:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {serviceArea.regionCode} (Germany)
                      </Typography>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Servicebereich-Informationen werden geladen...
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Service-Optionen / Dienstleistungen */}
          {attributes && (
            <Card sx={{
              mb: 3,
              backgroundColor: amDesignColors.white,
              borderRadius: '14px',
              border: `1px solid ${amDesignColors.border}`,
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em',
                    color: amDesignColors.text.primary
                  }}>
                    <ServiceIcon sx={{ fontSize: '1.1rem' }} /> Dienstleistungen
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editingAttributes ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setEditingAttributes(false);
                            setUpdatedAttributes({});
                            setUpdatedLanguages({ setValues: [], unsetValues: [] });
                            setUpdatedRepeatedEnums({});
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={updateAttributes}
                          disabled={updatingAttributes || (Object.keys(updatedAttributes).length === 0 && updatedLanguages.setValues.length === 0 && updatedLanguages.unsetValues.length === 0)}
                          startIcon={updatingAttributes ? <CircularProgress size={16} /> : <SaveAttributesIcon />}
                        >
                          {updatingAttributes ? 'Speichern...' : `Änderungen speichern (${Object.keys(updatedAttributes).length + updatedLanguages.setValues.length + updatedLanguages.unsetValues.length})`}
                        </Button>
                      </>
                    ) : !isReadOnly() ? (
                      <IconButton
                        size="small"
                        onClick={() => setEditingAttributes(true)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Helper function to format attribute names for display */}
                {(() => {
                  const getAttributeDisplayInfo = (attributeName) => {
                    const cleaned = attributeName.replace('attributes/', '');
                    
                    // First try to get German translation
                    const translationKey = `attributes.${cleaned}`;
                    const translatedName = t(translationKey);
                    
                    // If translation exists (different from key), use it
                    if (translatedName !== translationKey) {
                      return {
                        displayName: translatedName,
                        helpText: null,
                        groupDisplayName: null
                      };
                    }

                    // Fallback: Try to get from API metadata with German language
                    if (attributeMetadata?.attributes) {
                      const metadata = attributeMetadata.attributes.find(
                        attr => attr.name === attributeName || attr.name === cleaned
                      );
                      if (metadata?.displayName) {
                        return {
                          displayName: metadata.displayName,
                          helpText: metadata.helpText || null,
                          groupDisplayName: metadata.groupDisplayName || null
                        };
                      }
                    }

                    // Final fallback: Generate display name from attribute name
                    const fallbackName = cleaned
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

                    return {
                      displayName: fallbackName,
                      helpText: null,
                      groupDisplayName: null
                    };
                  };

                  return (
                    <Stack spacing={1}>
                      {attributes.map((attr) => {

                    // Display boolean attributes with toggle functionality
                    if (attr.valueType === 'BOOL') {
                      const currentValue = attr.values?.[0] === true;
                      const pendingValue = updatedAttributes.hasOwnProperty(attr.name) ? updatedAttributes[attr.name] : currentValue;
                      const hasChanges = updatedAttributes.hasOwnProperty(attr.name);
                      
                      return (
                        <Box key={attr.name} sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {pendingValue ? (
                              <CheckIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                            ) : (
                              <CloseIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />
                            )}
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: pendingValue ? 'success.main' : 'text.secondary',
                                fontWeight: pendingValue ? 500 : 400,
                                textDecoration: pendingValue ? 'none' : 'line-through',
                                fontStyle: hasChanges ? 'italic' : 'normal'
                              }}
                            >
                              {getAttributeDisplayInfo(attr.name).displayName}
                              {hasChanges && (
                                <Typography component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>
                                  (Geändert)
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                          
                          {editingAttributes && (
                            <Switch
                              checked={pendingValue}
                              onChange={() => handleAttributeToggle(attr.name, currentValue)}
                              size="small"
                              color="primary"
                            />
                          )}
                        </Box>
                      );
                    }
                        return null;
                      }).filter(Boolean)}

                      {/* Non-Boolean Attributes (URL, ENUM, etc.) */}
                      {attributes
                        .filter(attr => attr.valueType !== 'BOOL' && !attr.name.includes('languages_spoken'))
                        .map((attr) => {
                          // Display URL attributes
                          if (attr.valueType === 'URL' && attr.uriValues?.[0]?.uri) {
                            return (
                              <Box key={attr.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <LinkIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                                <Box>
                                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                    {getAttributeDisplayInfo(attr.name).displayName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    {attr.uriValues[0].uri}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          }
                          
                          // Display ENUM attributes
                          if (attr.valueType === 'ENUM' && attr.values?.[0]) {
                            return (
                              <Box key={attr.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <InfoIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                                <Box>
                                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                    {getAttributeDisplayInfo(attr.name).displayName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    {attr.values[0].replace(/_/g, ' ').split(' ').map(word => 
                                      word.charAt(0).toUpperCase() + word.slice(1)
                                    ).join(' ')}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          }
                          
                          // Display REPEATED_ENUM attributes
                          if (attr.valueType === 'REPEATED_ENUM' && (attr.repeatedEnumValue?.setValues?.length > 0 || attr.repeatedEnumValue?.unsetValues?.length > 0)) {
                            const formatValues = (values, attributeName) => values?.map(value => 
                              formatValueName(value, attributeName)
                            ).join(', ') || '';

                            const formatValueName = (value, attributeName) => {
                              // Try to get German translation for the value
                              const translationKey = `attributes.${value}`;
                              const translatedValue = t(translationKey);
                              
                              // If translation exists (different from key), use it
                              if (translatedValue !== translationKey) {
                                return translatedValue;
                              }
                              
                              // Try to get display name from API metadata
                              if (attributeMetadata?.attributes) {
                                const metadata = attributeMetadata.attributes.find(
                                  attr => attr.name === attributeName || attr.name === attributeName.replace('attributes/', '')
                                );
                                
                                if (metadata?.valueMetadata) {
                                  const valueMetadata = metadata.valueMetadata.find(vm => vm.value === value);
                                  if (valueMetadata?.displayName) {
                                    return valueMetadata.displayName;
                                  }
                                }
                              }
                              
                              // Fallback: format the value name
                              return value.replace(/_/g, ' ').split(' ').map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ');
                            };

                            // Get all possible values for this attribute
                            const allValues = [
                              ...(attr.repeatedEnumValue.setValues || []),
                              ...(attr.repeatedEnumValue.unsetValues || [])
                            ];

                            // Get current state with any pending changes
                            const currentState = updatedRepeatedEnums[attr.name] || {
                              setValues: attr.repeatedEnumValue.setValues || [],
                              unsetValues: attr.repeatedEnumValue.unsetValues || []
                            };

                            const toggleValue = (value) => {
                              const isCurrentlySet = currentState.setValues.includes(value);
                              
                              if (isCurrentlySet) {
                                // Move from setValues to unsetValues
                                setUpdatedRepeatedEnums(prev => ({
                                  ...prev,
                                  [attr.name]: {
                                    setValues: currentState.setValues.filter(v => v !== value),
                                    unsetValues: [...currentState.unsetValues.filter(v => v !== value), value]
                                  }
                                }));
                              } else {
                                // Move from unsetValues to setValues
                                setUpdatedRepeatedEnums(prev => ({
                                  ...prev,
                                  [attr.name]: {
                                    setValues: [...currentState.setValues.filter(v => v !== value), value],
                                    unsetValues: currentState.unsetValues.filter(v => v !== value)
                                  }
                                }));
                              }
                            };

                            return (
                              <Box key={attr.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                <InfoIcon sx={{ color: 'primary.main', fontSize: '1.2rem', mt: 0.5 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, mb: 1 }}>
                                    {getAttributeDisplayInfo(attr.name).displayName}
                                  </Typography>

                                  {editingAttributes ? (
                                    // Edit mode: Show toggleable chips for all values
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {allValues.map(value => {
                                        const isAccepted = currentState.setValues.includes(value);
                                        const hasChanges = updatedRepeatedEnums[attr.name] && 
                                          ((updatedRepeatedEnums[attr.name].setValues.includes(value)) !== 
                                           (attr.repeatedEnumValue.setValues?.includes(value) || false));

                                        return (
                                          <Chip
                                            key={value}
                                            label={formatValueName(value, attr.name)}
                                            onClick={() => toggleValue(value)}
                                            color={isAccepted ? "success" : "error"}
                                            variant={isAccepted ? "filled" : "outlined"}
                                            size="small"
                                            sx={{
                                              cursor: 'pointer',
                                              fontStyle: hasChanges ? 'italic' : 'normal',
                                              '&:hover': {
                                                opacity: 0.8
                                              }
                                            }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  ) : (
                                    // View mode: Show current values
                                    <>
                                      {/* Accepted/Set Values */}
                                      {currentState.setValues?.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                          <CheckIcon sx={{ color: 'success.main', fontSize: '0.875rem' }} />
                                          <Typography variant="caption" color="success.main" sx={{ fontSize: '0.75rem' }}>
                                            Accepted: {formatValues(currentState.setValues, attr.name)}
                                          </Typography>
                                        </Box>
                                      )}
                                      
                                      {/* Not Accepted/Unset Values */}
                                      {currentState.unsetValues?.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                          <CloseIcon sx={{ color: 'error.main', fontSize: '0.875rem' }} />
                                          <Typography variant="caption" color="error.main" sx={{ fontSize: '0.75rem' }}>
                                            Not Accepted: {formatValues(currentState.unsetValues, attr.name)}
                                          </Typography>
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </Box>
                              </Box>
                            );
                          }
                          
                          return null;
                        }).filter(Boolean)}

                      {/* Languages Spoken */}
                  {attributes.find(attr => attr.name === 'attributes/languages_spoken') && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Languages Spoken:
                      </Typography>
                      
                      {editingAttributes ? (
                        // Edit mode: Show all languages with toggle switches
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {(() => {
                            const langAttr = attributes.find(attr => attr.name === 'attributes/languages_spoken');
                            
                            // Get all possible language values from current attribute or metadata
                            const getAllLanguages = () => {
                              if (langAttr?.repeatedEnumValue) {
                                return [
                                  ...(langAttr.repeatedEnumValue.setValues || []),
                                  ...(langAttr.repeatedEnumValue.unsetValues || [])
                                ];
                              }
                              
                              // Try to get from metadata
                              const languageMetadata = attributeMetadata?.attributes?.find(
                                attr => attr.name === 'languages_spoken' || attr.name === 'attributes/languages_spoken'
                              );
                              
                              if (languageMetadata?.valueMetadata) {
                                return languageMetadata.valueMetadata.map(vm => vm.value);
                              }
                              
                              // Fallback: return empty array if no data available
                              return [];
                            };
                            
                            const allLanguages = getAllLanguages();
                            
                            return allLanguages.map((lang) => {
                              const isCurrentlyActive = langAttr?.repeatedEnumValue?.setValues?.includes(lang) || false;
                              const isBeingAdded = updatedLanguages.setValues.includes(lang);
                              const isBeingRemoved = updatedLanguages.unsetValues.includes(lang);
                              const finalState = isCurrentlyActive ? !isBeingRemoved : isBeingAdded;
                              const hasChanges = isBeingAdded || isBeingRemoved;
                              
                              // Get display name using translation system
                              const getLanguageDisplayName = (langValue) => {
                                // Try German translation first
                                const translationKey = `attributes.${langValue}`;
                                const translatedName = t(translationKey);
                                
                                if (translatedName !== translationKey) {
                                  return translatedName;
                                }
                                
                                // Try API metadata
                                const languageMetadata = attributeMetadata?.attributes?.find(
                                  attr => attr.name === 'languages_spoken' || attr.name === 'attributes/languages_spoken'
                                );
                                
                                if (languageMetadata?.valueMetadata) {
                                  const valueMetadata = languageMetadata.valueMetadata.find(vm => vm.value === langValue);
                                  if (valueMetadata?.displayName) {
                                    return valueMetadata.displayName;
                                  }
                                }
                                
                                // Fallback: format the value name
                                return langValue.replace(/_/g, ' ').replace(' spoken', '').replace(' used', '').trim()
                                  .split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                              };
                              
                              const displayName = getLanguageDisplayName(lang);
                              
                              return (
                                <Box key={lang} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: finalState ? 'success.main' : 'text.secondary',
                                        fontStyle: hasChanges ? 'italic' : 'normal',
                                        fontWeight: finalState ? 500 : 400,
                                        textDecoration: finalState ? 'none' : 'line-through'
                                      }}
                                    >
                                      {displayName}
                                    </Typography>
                                    {hasChanges && (
                                      <Typography variant="caption" sx={{ color: 'warning.main' }}>
                                        (Geändert)
                                      </Typography>
                                    )}
                                  </Box>
                                  <Switch
                                    checked={finalState}
                                    onChange={() => handleLanguageToggle(lang, isCurrentlyActive)}
                                    size="small"
                                    color="primary"
                                  />
                                </Box>
                              );
                            });
                          })()}
                        </Box>
                      ) : (
                        // View mode: Show all languages with visual indicators
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(() => {
                            const langAttr = attributes.find(attr => attr.name === 'attributes/languages_spoken');
                            
                            // Get all possible language values from current attribute or metadata
                            const getAllLanguages = () => {
                              if (langAttr?.repeatedEnumValue) {
                                return [
                                  ...(langAttr.repeatedEnumValue.setValues || []),
                                  ...(langAttr.repeatedEnumValue.unsetValues || [])
                                ];
                              }
                              
                              // Try to get from metadata
                              const languageMetadata = attributeMetadata?.attributes?.find(
                                attr => attr.name === 'languages_spoken' || attr.name === 'attributes/languages_spoken'
                              );
                              
                              if (languageMetadata?.valueMetadata) {
                                return languageMetadata.valueMetadata.map(vm => vm.value);
                              }
                              
                              // Fallback: return empty array if no data available
                              return [];
                            };
                            
                            const allLanguages = getAllLanguages();
                            
                            return allLanguages.map((lang, index) => {
                              const isActive = langAttr?.repeatedEnumValue?.setValues?.includes(lang) || false;
                              // Get display name using translation system
                              const getLanguageDisplayName = (langValue) => {
                                // Try German translation first
                                const translationKey = `attributes.${langValue}`;
                                const translatedName = t(translationKey);
                                
                                if (translatedName !== translationKey) {
                                  return translatedName;
                                }
                                
                                // Try API metadata
                                const languageMetadata = attributeMetadata?.attributes?.find(
                                  attr => attr.name === 'languages_spoken' || attr.name === 'attributes/languages_spoken'
                                );
                                
                                if (languageMetadata?.valueMetadata) {
                                  const valueMetadata = languageMetadata.valueMetadata.find(vm => vm.value === langValue);
                                  if (valueMetadata?.displayName) {
                                    return valueMetadata.displayName;
                                  }
                                }
                                
                                // Fallback: format the value name
                                return langValue.replace(/_/g, ' ').replace(' spoken', '').replace(' used', '').trim()
                                  .split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                              };
                              
                              const displayName = getLanguageDisplayName(lang);
                              
                              return (
                                <Chip
                                  key={index}
                                  label={displayName}
                                  variant={isActive ? "filled" : "outlined"}
                                  color={isActive ? "success" : "default"}
                                  size="small"
                                  sx={{
                                    textDecoration: isActive ? 'none' : 'line-through',
                                    opacity: isActive ? 1 : 0.6,
                                    '& .MuiChip-label': {
                                      textDecoration: isActive ? 'none' : 'line-through'
                                    }
                                  }}
                                />
                              );
                            });
                          })()}
                        </Box>
                      )}
                    </Box>
                  )}
                    </Stack>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Metadata Section */}
      <Card sx={{ mt: 3, backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Metadata
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>


             {businessData.googleAccountId && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Google Account ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", mt: 0.5 }}>
                  {businessData.googleAccountId}
                </Typography>
              </Grid>
            )}








            {businessData.googleLocationId && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Google Location ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", mt: 0.5 }}>
                  {businessData.googleLocationId}
                </Typography>
              </Grid>
            )}

            {businessData.placeId && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Place ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", mt: 0.5 }}>
                  {businessData.placeId}
                </Typography>
              </Grid>
            )}


            {businessData.realPlaceId && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Real Place ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", mt: 0.5 }}>
                  {businessData.realPlaceId}
                </Typography>
              </Grid>
            )}



            


            {businessData.createdAt && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {new Date(businessData.createdAt).toLocaleDateString()}
                </Typography>
              </Grid>
            )}

            {businessData.updatedAt && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {new Date(businessData.updatedAt).toLocaleDateString()}
                </Typography>
              </Grid>
            )}

{businessData.keywords && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Total keywords
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {businessData.keywords.length}
                </Typography>
              </Grid>
            )}



            {businessData.keywords && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Total Snapshots
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {businessData.rankingSnapshots.length}
                </Typography>
              </Grid>
            )}



            

          </Grid>
        </CardContent>
      </Card>
      </>
      )}

      {/* Services Tab Content */}
      {topLevelTab === 1 && (
        <ServiceManager />
      )}
    </Container>
  );
};

export default ProfilePage;
